/**
 * TeamsTable — V2 canonical table built per the `table-style` skill.
 * Mode A (fully client-side) since the teams dataset is small (<20 rows).
 *
 * - ColumnDef-driven (filter row, sortable headers, column visibility)
 * - Always-visible per-column filter row
 * - Copy / CSV / Refresh / Columns toolbar
 * - Row click → opens SlidePanel via parent's onRowClick(team)
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Copy, Download, Settings2, RefreshCw, FilterX,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTeams, teamKeys } from '@/features/rocks/hooks/useTeams';
import { getTeamMembers } from '@/features/rocks/api';
import { useUsers } from '@/features/users/hooks';
import type { Team } from '../types';
import type { User } from '@/features/users/types';

interface ColumnDef {
  key: string;
  label: string;
  sortKey?: string;
  filterType?: 'text';
  getValue: (row: TeamRow) => string | number | null;
  render?: (row: TeamRow) => React.ReactNode;
  align?: 'left' | 'right';
  noFilter?: boolean;
  defaultVisible?: boolean;
}

interface TeamRow {
  id: string;
  name: string;
  membersCount: number;
  leaderNames: string;
  memberNames: string;
  members: { userId: string; role: string }[];
}

const VISIBILITY_KEY = 'atek-teams-column-visibility';
const FILTERS_KEY = 'atek-teams-column-filters';

function displayName(user: User): string {
  return [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email;
}

interface Props {
  onRowClick: (team: Team) => void;
  onCreate: () => void;
}

export function TeamsTable({ onRowClick, onCreate }: Props) {
  const qc = useQueryClient();
  const { data: teams = [], isLoading, isFetching } = useTeams();
  // Internal staff only — EOS teams contain employees, never customer users.
  // Previously this used `organisation: sessionUser.organisation` but V4
  // sessions don't carry an organisation, so the request was unscoped and
  // returned the first 100 users alphabetically (cust + prospects), missing
  // most employees — which made leader/member names render as raw UUIDs.
  const { data: usersData } = useUsers({
    employment_type: 'employee',
    pageLength: 100,
    sortKey: 'firstname',
    enabled: 'true',
  });
  const users = usersData?.users ?? [];

  // PG UUID → User (post 0198 — team_members.user_id is PG UUID)
  const userMap = useMemo(
    () => new Map<string, User>(
      users.filter((u) => u.pg_id).map((u) => [u.pg_id as string, u]),
    ),
    [users],
  );

  // Fan-out: fetch members for every team
  const memberQueries = useQueries({
    queries: teams.map((team) => ({
      queryKey: teamKeys.members(team.id),
      queryFn: () => getTeamMembers(team.id),
    })),
  });
  const membersByTeam = useMemo(() => {
    const map = new Map<string, { userId: string; role: string }[]>();
    teams.forEach((t, i) => {
      const data = memberQueries[i]?.data;
      if (data) map.set(t.id, data.map((m) => ({ userId: m.userId, role: m.role })));
    });
    return map;
  }, [teams, memberQueries]);

  // Build TeamRow[] for the table
  const rows: TeamRow[] = useMemo(() => teams.map((team) => {
    const members = membersByTeam.get(team.id) ?? [];
    const leaderNames = members
      .filter((m) => m.role === 'leader')
      .map((m) => userMap.get(m.userId))
      .map((u) => u ? displayName(u) : '?')
      .join(', ');
    const memberNames = members
      .map((m) => userMap.get(m.userId))
      .map((u) => u ? displayName(u) : '?')
      .join(', ');
    return {
      id: team.id,
      name: team.name,
      membersCount: members.length,
      leaderNames,
      memberNames,
      members,
    };
  }), [teams, membersByTeam, userMap]);

  // Column visibility (persisted)
  const COLUMNS: ColumnDef[] = useMemo(() => [
    { key: 'name', label: 'Team', sortKey: 'name', filterType: 'text', getValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'leader', label: 'Leader', filterType: 'text', getValue: (r) => r.leaderNames,
      render: (r) => {
        const leader = r.members.find((m) => m.role === 'leader');
        if (!leader) return <span className="text-xs text-muted-foreground">—</span>;
        const user = userMap.get(leader.userId);
        return (
          <div className="flex items-center gap-2">
            <OwnerAvatar
              ownerId={user?._id}
              photoUrl={user?.profile_photo_url}
              name={user ? displayName(user) : '?'}
              className="h-7 w-7"
            />
            <span className="text-sm">{user ? displayName(user) : leader.userId}</span>
          </div>
        );
      } },
    { key: 'members', label: 'Members', filterType: 'text', getValue: (r) => r.memberNames,
      render: (r) => {
        if (r.members.length === 0) return <span className="text-xs text-muted-foreground">No members</span>;
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider delayDuration={200}>
              <div className="flex -space-x-2">
                {r.members.slice(0, 6).map((m) => {
                  const user = userMap.get(m.userId);
                  return (
                    <Tooltip key={m.userId}>
                      <TooltipTrigger asChild>
                        <div className={cn('rounded-full ring-2 ring-background', m.role === 'leader' && 'ring-amber-400')}>
                          <OwnerAvatar
                            ownerId={user?._id}
                            photoUrl={user?.profile_photo_url}
                            name={user ? displayName(user) : '?'}
                            className="h-7 w-7"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {user ? displayName(user) : m.userId}
                        {m.role === 'leader' && ' (Leader)'}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
                {r.members.length > 6 && (
                  <div className="h-7 w-7 rounded-full bg-muted ring-2 ring-background flex items-center justify-center text-[10px] font-medium text-muted-foreground">
                    +{r.members.length - 6}
                  </div>
                )}
              </div>
            </TooltipProvider>
            <span className="text-xs text-muted-foreground tabular-nums">{r.membersCount}</span>
          </div>
        );
      } },
    { key: 'actions', label: '', noFilter: true, getValue: () => '',
      render: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />, align: 'right' },
  ], [userMap]);

  const defaultVisibility: Record<string, boolean> = useMemo(() => {
    const d: Record<string, boolean> = {};
    for (const c of COLUMNS) d[c.key] = c.defaultVisible !== false;
    return d;
  }, [COLUMNS]);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(VISIBILITY_KEY);
      return stored ? { ...defaultVisibility, ...JSON.parse(stored) } : defaultVisibility;
    } catch { return defaultVisibility; }
  });
  useEffect(() => { try { localStorage.setItem(VISIBILITY_KEY, JSON.stringify(columnVisibility)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnVisibility]);
  const isVisible = (k: string) => columnVisibility[k] !== false;
  const toggleColumn = (k: string) => setColumnVisibility((v) => ({ ...v, [k]: !isVisible(k) }));
  const visibleColumns = COLUMNS.filter((c) => isVisible(c.key));

  // Column filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(columnFilters)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnFilters]);
  const setColumnFilter = (k: string, v: string) => setColumnFilters((p) => ({ ...p, [k]: v }));
  const clearFilters = () => setColumnFilters({});
  const hasActiveFilters = Object.values(columnFilters).some(Boolean);

  // Sort
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const handleSort = (k: string) => {
    if (k === sortKey) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDirection(1); }
  };

  // Combined filter + sort
  const filtered = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return rows;
    return rows.filter((row) =>
      active.every(([key, value]) => {
        const col = COLUMNS.find((c) => c.key === key);
        if (!col || col.noFilter) return true;
        return String(col.getValue(row) ?? '').toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [rows, columnFilters, COLUMNS]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.getValue(a); const bv = col.getValue(b);
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDirection * (av - bv);
      return sortDirection * String(av).localeCompare(String(bv));
    });
  }, [filtered, sortKey, sortDirection, COLUMNS]);

  // Export helpers
  function escapeCSV(v: unknown): string {
    if (v == null) return '';
    let s = String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }
  const exportCols = COLUMNS.filter((c) => !c.noFilter);
  const handleCopy = () => {
    const header = exportCols.map((c) => c.label).join('\t');
    const body = sorted.map((r) => exportCols.map((c) => String(c.getValue(r) ?? '')).join('\t'));
    navigator.clipboard.writeText([header, ...body].join('\n'));
    toast.success('Copied to clipboard');
  };
  const handleCSV = () => {
    const header = exportCols.map((c) => escapeCSV(c.label)).join(',');
    const body = sorted.map((r) => exportCols.map((c) => escapeCSV(c.getValue(r))).join(','));
    const blob = new Blob([[header, ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `teams_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sorted.length} rows`);
  };
  const handleRefresh = () => qc.invalidateQueries({ queryKey: teamKeys.all });

  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">
          {filtered.length < rows.length
            ? `Showing ${filtered.length} of ${rows.length}`
            : `${rows.length} team${rows.length !== 1 ? 's' : ''}`}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} /> Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key} checked={isVisible(c.key)} onCheckedChange={() => toggleColumn(c.key)}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1" /> New Team
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* Filter row */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => (
                  <TableHead key={`f-${col.key}`} className="p-1">
                    {col.noFilter ? null : (
                      <Input
                        className="h-7 text-xs"
                        value={columnFilters[col.key] || ''}
                        onChange={(e) => setColumnFilter(col.key, e.target.value)}
                        placeholder="…"
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
              {/* Header row */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <Button
                        variant="ghost" size="sm"
                        className="-ml-3 h-8 hover:bg-transparent"
                        onClick={() => handleSort(col.sortKey as string)}
                      >
                        {col.label}
                        {sortKey === col.sortKey ? (
                          sortDirection === 1
                            ? <ArrowUp className="ml-1 h-3 w-3" />
                            : <ArrowDown className="ml-1 h-3 w-3" />
                        ) : <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
                      </Button>
                    ) : col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`s-${i}`}>
                    {visibleColumns.map((c) => (
                      <TableCell key={c.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-sm text-muted-foreground">
                    No teams match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      const t = teamById.get(row.id);
                      if (t) onRowClick(t);
                    }}
                  >
                    {visibleColumns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={cn('text-sm', c.align === 'right' && 'text-right tabular-nums')}
                      >
                        {c.render ? c.render(row) : (String(c.getValue(row) ?? '') || '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default TeamsTable;
